import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DragField } from './DragField';
import { Layers, TrendingUp, Sparkles, Star, ChevronRight, ChevronDown } from 'lucide-react';
import { api } from '@/lib/api';

// Stable identity for a palette field used to persist Favourites.
const fieldKey = (f) => `${f._isModelMeasure ? 'calc' : (f.semantic_type || 'field')}::${f.name}`;

// Displayed label for a field — mirrors DragField so sorting matches the UI.
const fieldLabel = (f) => (f._baseLabel || f.business_name || (f.name || '').replace(/_/g, ' ')).toString();

function groupByEntity(fields) {
  const groups = [];
  const seen = new Set();
  for (const f of fields) {
    const eid = f.entity_name || '_ungrouped';
    if (!seen.has(eid)) {
      seen.add(eid);
      groups.push({ entityName: f.entity_name || 'Other', entityLogicalName: f.entity_logical_name || '', fields: [] });
    }
    groups.find(g => g.entityName === (f.entity_name || 'Other')).fields.push(f);
  }
  // Tables alphabetical (by displayed name), and fields alphabetical within each table.
  const coll = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
  groups.sort((a, b) => coll(a.entityLogicalName || a.entityName, b.entityLogicalName || b.entityName));
  groups.forEach(g => g.fields.sort((a, b) => coll(fieldLabel(a), fieldLabel(b))));
  return groups;
}

function EntityGroup({ group, collapsed, onToggle, onBrowse, onAdd, onViewData, onBin, highlight, idPrefix = '', favKeys, onToggleFav }) {
  const displayName = group.entityLogicalName || group.entityName;
  return (
    <div>
      <button onClick={onToggle} className="w-full flex items-center gap-1 px-2 py-1 text-[11px] text-muted-foreground/70 hover:text-muted-foreground transition-colors" data-testid={`entity-group-${idPrefix}${group.entityName}`}>
        {collapsed ? <ChevronRight className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
        <span className="font-medium truncate">{displayName}</span>
        <span className="text-[8px] text-muted-foreground/70 ml-auto">{group.fields.length}</span>
      </button>
      {!collapsed && group.fields.map(f => <DragField key={f.name} field={f} onBrowse={onBrowse} onAddToVisual={onAdd} onViewData={onViewData} onBin={onBin} highlight={highlight} onToggleFav={onToggleFav} isFav={favKeys?.has(fieldKey(f))} />)}
    </div>
  );
}

export function FieldSplitPanel({ filteredDims, filteredMsrs, filteredCalcMsrs, handleBrowseField, handleAddFieldToVisual, handleViewField, handleBinField, highlight, modelId }) {
  const [splitPct, setSplitPct] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const [collapsedEntities, setCollapsedEntities] = useState(new Set());

  // Author-pinned Favourites, persisted per model in the browser.
  // Pinned fields live on the user's profile (cross-device), keyed by model.
  const [favKeys, setFavKeys] = useState(new Set());
  useEffect(() => {
    let cancelled = false;
    if (!modelId) { setFavKeys(new Set()); return; }
    api.getFieldFavorites(modelId)
      .then(r => { if (!cancelled) setFavKeys(new Set((r && r.keys) || [])); })
      .catch(() => { if (!cancelled) setFavKeys(new Set()); });
    return () => { cancelled = true; };
  }, [modelId]);
  const toggleFav = useCallback((f) => {
    const k = fieldKey(f);
    setFavKeys(prev => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      if (modelId) api.setFieldFavorites(modelId, [...n]).catch(() => { /* best-effort sync */ });
      return n;
    });
  }, [modelId]);
  const [favCollapsed, setFavCollapsed] = useState(false);

  const dimGroups = useMemo(() => groupByEntity(filteredDims), [filteredDims]);
  const msrGroups = useMemo(() => groupByEntity(filteredMsrs), [filteredMsrs]);
  // Calc measures group by display folder (falling back to their source table).
  const calcGroups = useMemo(() => {
    const groups = []; const idx = new Map();
    for (const f of filteredCalcMsrs) {
      const key = f.display_folder || f.entity_logical_name || f.entity_name || 'General';
      if (!idx.has(key)) { idx.set(key, groups.length); groups.push({ entityName: key, entityLogicalName: '', fields: [] }); }
      groups[idx.get(key)].fields.push(f);
    }
    // Sort folders, and fields within each folder, alphabetically — matching
    // how Dimensions and Measures are ordered (see groupByEntity).
    const coll = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
    groups.sort((a, b) => coll(a.entityName, b.entityName));
    groups.forEach(g => g.fields.sort((a, b) => coll(fieldLabel(a), fieldLabel(b))));
    return groups;
  }, [filteredCalcMsrs]);

  // Flat Favourites list drawn from the currently visible (search-filtered) fields.
  const favFields = useMemo(() => {
    const all = [...filteredDims, ...filteredMsrs, ...filteredCalcMsrs];
    const seen = new Set(); const out = [];
    for (const f of all) {
      const k = fieldKey(f);
      if (favKeys.has(k) && !seen.has(k)) { seen.add(k); out.push(f); }
    }
    return out;
  }, [filteredDims, filteredMsrs, filteredCalcMsrs, favKeys]);

  const toggleEntity = useCallback((name) => {
    setCollapsedEntities(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
    const onMove = (ev) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = Math.max(15, Math.min(85, ((ev.clientY - rect.top) / rect.height) * 100));
      setSplitPct(pct);
    };
    const onUp = () => {
      setDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="field-split-panel">
      {/* Favourites — author-pinned fields, kept at the top */}
      {favFields.length > 0 && (
        <div className="flex-shrink-0 flex flex-col overflow-hidden border-b border-border/40 max-h-[32%]" data-testid="favourites-section">
          <button onClick={() => setFavCollapsed(c => !c)} className="w-full px-2 py-1.5 flex-shrink-0 flex items-center gap-1" data-testid="favourites-toggle">
            {favCollapsed ? <ChevronRight className="w-2.5 h-2.5 text-amber-400/80" /> : <ChevronDown className="w-2.5 h-2.5 text-amber-400/80" />}
            <p className="text-[9px] text-amber-400/90 uppercase tracking-wider font-semibold flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-amber-400" /> Favourites ({favFields.length})</p>
          </button>
          {!favCollapsed && (
            <ScrollArea className="flex-1">
              <div className="px-1 pb-1">
                {favFields.map(f => <DragField key={fieldKey(f)} field={f} onBrowse={handleBrowseField} onAddToVisual={handleAddFieldToVisual} onViewData={handleViewField} onBin={handleBinField} highlight={highlight} onToggleFav={toggleFav} isFav />)}
              </div>
            </ScrollArea>
          )}
        </div>
      )}

      <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* Dimensions */}
        <div style={{ height: `${splitPct}%` }} className="flex flex-col overflow-hidden">
          <div className="px-2 py-1.5 flex-shrink-0">
            <p className="text-[9px] text-emerald-400/70 uppercase tracking-wider font-semibold flex items-center gap-1"><Layers className="w-2.5 h-2.5" /> Dimensions ({filteredDims.length})</p>
          </div>
          <ScrollArea className="flex-1">
            <div className="px-1 pb-1">
              {dimGroups.length === 0 ? (
                <p className="text-[9px] text-muted-foreground/60 px-2 py-1">No dimensions</p>
              ) : (
                dimGroups.map(g => <EntityGroup key={g.entityName} group={g} collapsed={collapsedEntities.has('dim_' + g.entityName)} onToggle={() => toggleEntity('dim_' + g.entityName)} onBrowse={handleBrowseField} onAdd={handleAddFieldToVisual} onViewData={handleViewField} onBin={handleBinField} highlight={highlight} favKeys={favKeys} onToggleFav={toggleFav} />)
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Draggable Divider */}
        <div
          onMouseDown={handleMouseDown}
          className={`flex-shrink-0 h-2 flex items-center justify-center cursor-row-resize border-y transition-colors ${dragging ? 'border-primary/40 bg-primary/10' : 'border-border/30 hover:border-primary/20 hover:bg-primary/5'}`}
          data-testid="split-divider"
        >
          <div className={`w-8 h-0.5 rounded-full transition-colors ${dragging ? 'bg-primary' : 'bg-muted-foreground/20'}`} />
        </div>

        {/* Measures */}
        <div style={{ height: `${100 - splitPct}%` }} className="flex flex-col overflow-hidden">
          <div className="px-2 py-1.5 flex-shrink-0">
            <p className="text-[9px] text-primary/70 uppercase tracking-wider font-semibold flex items-center gap-1"><TrendingUp className="w-2.5 h-2.5" /> Measures ({filteredMsrs.length + filteredCalcMsrs.length})</p>
          </div>
          <ScrollArea className="flex-1">
            <div className="px-1 pb-1">
              {msrGroups.length === 0 ? (
                filteredMsrs.length === 0 && <p className="text-[9px] text-muted-foreground/60 px-2 py-1">No measures</p>
              ) : (
                msrGroups.map(g => <EntityGroup key={g.entityName} group={g} collapsed={collapsedEntities.has('msr_' + g.entityName)} onToggle={() => toggleEntity('msr_' + g.entityName)} onBrowse={handleBrowseField} onAdd={handleAddFieldToVisual} onViewData={handleViewField} onBin={handleBinField} highlight={highlight} favKeys={favKeys} onToggleFav={toggleFav} />)
              )}
              {filteredCalcMsrs.length > 0 && (
                <div className="mt-1 pt-1 border-t border-border/20">
                  <p className="text-[9px] text-amber-400/70 uppercase tracking-wider font-semibold mb-1 px-2 flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> Calculations ({filteredCalcMsrs.length})</p>
                  {calcGroups.map(g => <EntityGroup key={g.entityName} group={g} idPrefix="calc-" collapsed={collapsedEntities.has('calc_' + g.entityName)} onToggle={() => toggleEntity('calc_' + g.entityName)} onBrowse={handleBrowseField} onAdd={handleAddFieldToVisual} onViewData={handleViewField} highlight={highlight} favKeys={favKeys} onToggleFav={toggleFav} />)}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
